import { useCallback, useEffect, useState } from "react";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Copy, KeyIcon } from "./icons";

const PROVIDERS = [
  { key: "xero", name: "Xero", where: "developer.xero.com → My Apps → New app (Web app)", scopes: "openid profile email accounting.transactions accounting.contacts accounting.settings offline_access" },
  { key: "quickbooks", name: "QuickBooks Online", where: "developer.intuit.com → Dashboard → Create an app → Keys & credentials (Development keys = sandbox, Production keys = live)", scopes: "com.intuit.quickbooks.accounting", environments: true },
  { key: "freeagent", name: "FreeAgent", where: "dev.freeagent.com → My apps → Create new app (use the sandbox app for testing)", scopes: "full company access (FreeAgent has no per-scope consent)", environments: true },
  { key: "sage", name: "Sage Accounting", where: "developerselfservice.sageone.com → Create app → copy Client ID / secret and add the redirect URI (Sage Business Cloud Accounting, UK)", scopes: "full_access" },
  { key: "zoho_books", name: "Zoho Books", where: "api-console.zoho.com → Add client → Server-based application. Pick the data centre your Zoho account lives in (UK accounts are on EU)", scopes: "ZohoBooks.fullaccess.all", environments: true },
  { key: "freshbooks", name: "FreshBooks", where: "my.freshbooks.com → Settings → Developer Portal → Create an app (Web) and add the redirect URI", scopes: "user:profile, clients, invoices, payments, expenses, journal entries (set on the app)" },
  { key: "google_calendar", name: "Google Calendar", where: "Google Cloud Console → APIs & Services → Credentials → OAuth client ID (Web). Enable the Google Calendar API.", scopes: "calendar.events + email" },
  { key: "microsoft_calendar", name: "Outlook / Microsoft 365", where: "portal.azure.com → App registrations → New (accounts in any org + personal) → Certificates & secrets", scopes: "Calendars.ReadWrite offline_access User.Read" },
];

function ProviderRow({ p, state, endpoint, onChange }) {
  const [cid, setCid] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [env, setEnv] = useState(state?.environment || "production");
  useEffect(() => { if (state?.environment) setEnv(state.environment); }, [state?.environment]);

  const save = async () => {
    setBusy(true);
    try {
      await API.put(`${endpoint}/${p.key}`, { client_id: cid || undefined, client_secret: secret || undefined, ...(p.environments ? { environment: env } : {}) });
      toast.success(`${p.name} app credentials saved`);
      setCid(""); setSecret(""); setOpen(false); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    if (!window.confirm(`Remove the ${p.name} app credentials saved here?`)) return;
    setBusy(true);
    try { await API.put(`${endpoint}/${p.key}`, { clear: true }); toast.success("Removed"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
    finally { setBusy(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(state.redirect_uri); toast.success("Redirect URI copied"); } catch { window.prompt("Copy:", state.redirect_uri); } };

  const srcLabel = { tenant: "your own app", platform: "platform app", env: "platform app (deployment)", none: "not configured" }[state?.source] || state?.source;
  return (
    <div className="rounded-md border border-border p-3 space-y-2" data-testid={`app-creds-${p.key}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">{p.name}</p>
        <Badge variant={state?.configured ? "default" : "secondary"} data-testid={`app-creds-${p.key}-status`}>{state?.configured ? `Configured — ${srcLabel}` : "Not configured"}</Badge>
      </div>
      <p className="text-[10px] text-muted-foreground">{p.where}. Scopes: {p.scopes}.</p>
      <div className="flex items-center gap-2 text-[10px]">
        <span className="text-muted-foreground">Redirect URI:</span>
        <code className="break-all flex-1" data-testid={`app-creds-${p.key}-redirect`}>{state?.redirect_uri}</code>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={copy} data-testid={`app-creds-${p.key}-copy-redirect`}><Copy /></Button>
      </div>
      {state?.configured && state?.client_id_hint && <p className="text-[10px] text-muted-foreground">Client ID {state.client_id_hint}{p.environments && state?.environment ? ` · ${state.environment}` : ""}</p>}
      {open ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {p.environments && (
            <div className="sm:col-span-2 flex items-center gap-2 text-xs">
              <Label className="text-xs">{state?.environment_label || "Environment"}</Label>
              {(state?.environments || ["sandbox", "production"]).map((e) => (
                <Button key={e} size="sm" variant={env === e ? "default" : "outline"} className={`h-7 text-xs ${state?.environment_label ? "" : "capitalize"}`} onClick={() => setEnv(e)} data-testid={`app-creds-${p.key}-env-${e}`}>{e}</Button>
              ))}
            </div>
          )}
          <div><Label className="text-xs">Client ID</Label><Input value={cid} onChange={(e) => setCid(e.target.value)} className="mt-1 h-8 text-xs font-mono" data-testid={`app-creds-${p.key}-client-id`} /></div>
          <div><Label className="text-xs">Client secret</Label><Input type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} className="mt-1 h-8 text-xs font-mono" placeholder={state?.configured && state?.source !== "env" ? "•••••••• (saved)" : ""} data-testid={`app-creds-${p.key}-client-secret`} /></div>
          <div className="sm:col-span-2 flex gap-2">
            <Button size="sm" onClick={save} disabled={busy || !cid} data-testid={`app-creds-${p.key}-save`}>{busy ? "Saving…" : "Save"}</Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`app-creds-${p.key}-edit`}>{state?.configured && state?.source !== "env" ? "Replace credentials" : "Add credentials"}</Button>
          {state?.configured && state?.source !== "env" && <Button size="sm" variant="ghost" className="text-red-600" onClick={clear} disabled={busy} data-testid={`app-creds-${p.key}-clear`}>Remove</Button>}
        </div>
      )}
    </div>
  );
}

export default function AppCredentials({ endpoint, variant, onChange }) {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(variant === "platform");

  const load = useCallback(async () => {
    try { const { data } = await API.get(endpoint); setState(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load app credentials"); }
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChange && onChange(); };

  return (
    <section className="rounded-lg border border-border bg-card" data-testid={`app-credentials-${variant}`}>
      <button type="button" className="w-full flex items-center justify-between px-4 py-3 text-left" onClick={() => setOpen((o) => !o)} data-testid="app-credentials-toggle">
        <span className="text-sm font-semibold flex items-center gap-2"><KeyIcon /> {variant === "platform" ? "OAuth apps for tenant integrations" : "App credentials (advanced)"}</span>
        <span className="text-xs text-muted-foreground">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <p className="text-[11px] text-muted-foreground">
            {variant === "platform"
              ? "Register one app per provider under Fixlr and paste its Client ID/secret here. Every tenant can then connect with one click. Add BOTH redirect URIs (production and preview) to each app."
              : "Only needed if the platform hasn't provided one-click connect for a provider, or you'd rather use an app registered under your own company. Your credentials override the platform's for your workspace."}
          </p>
          <div className="grid gap-3 lg:grid-cols-3">
            {PROVIDERS.map((p) => <ProviderRow key={p.key} p={p} state={state?.[p.key]} endpoint={endpoint} onChange={refresh} />)}
          </div>
        </div>
      )}
    </section>
  );
}
